import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const AccessSecurityPage = () => {
  const [failedLoginsData, setFailedLoginsData] = useState(null);
  const [passwordResetsData, setPasswordResetsData] = useState(null);
  const [deviceUsageData, setDeviceUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFailedLogins();
    fetchPasswordResets();
    fetchDeviceUsage();
  }, []);

  const fetchFailedLogins = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/failed-login-attempts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFailedLoginsData(response.data);
    } catch (err) {
      console.error('Failed to fetch failed logins:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  const fetchPasswordResets = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/password-reset-frequency`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPasswordResetsData(response.data);
    } catch (err) {
      console.error('Failed to fetch password resets:', err);
    }
  };


  const fetchDeviceUsage = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/device-usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDeviceUsageData(response.data);
    } catch (err) {
      console.error('Failed to fetch device usage:', err);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading security analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Access & Security</h1>
          <p className="text-purple-200 text-sm">Login security and access pattern analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchFailedLogins}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Access & Security</h1>
              <p className="text-purple-200 text-sm">Login security and access pattern analytics</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchFailedLogins();
              fetchPasswordResets();
              fetchDeviceUsage();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Chart 1 (Item 10): Failed Login Attempts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Failed Login Attempts (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={failedLoginsData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" label={{ value: 'Failed Attempts', angle: -90, position: 'insideLeft' }} />
            <YAxis yAxisId="right" orientation="right" label={{ value: 'Failure Rate (%)', angle: 90, position: 'insideRight' }} />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="failed_attempts" stroke="#ef4444" strokeWidth={2} name="Failed Attempts" />
            <Line yAxisId="right" type="monotone" dataKey="failure_rate" stroke="#f97316" strokeWidth={2} name="Failure Rate %" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> Daily failed login attempts (red line) and failure rate percentage (orange line) over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Failure rate above 5% indicates UX issues (password complexity, confusing login flow). Sudden spikes may indicate security threats (brute force attempts) or system issues. Consistent low failure rate (less than 2%) is healthy. High failure rates require investigation - check if admins are forgetting passwords, entering wrong organisation IDs, or facing technical issues. Implement account lockout after multiple failures to prevent attacks.
          </p>
        </div>
      </div>



      {/* Chart 2 (Item 11): Password Reset Frequency */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Reset Frequency (Last 12 Months)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={passwordResetsData?.monthly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="resets" fill="#f59e0b" name="Password Resets" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The number of password reset requests from admin users each month over the last 12 months.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> High frequency (more than 10% of admins per month) suggests password requirements are too complex or admins aren't using password managers. Spikes may indicate security concerns (accounts compromised) or system changes (forced password updates). Low, consistent rates are healthy. If resets are trending upward, consider simplifying password requirements, implementing SSO, or providing password management guidance.
          </p>
        </div>
      </div>

      {/* Chart 3 (Item 12): Multi-Device Usage */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Multi-Device Usage Distribution (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={deviceUsageData?.device_usage || [
                {device: "Desktop", count: 0},
                {device: "Tablet", count: 0},
                {device: "Mobile", count: 0}
              ]}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ device, count, percent }) => `${device}: ${count} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={100}
              dataKey="count"
            >
              <Cell fill="#3b82f6" />
              <Cell fill="#10b981" />
              <Cell fill="#f59e0b" />
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The breakdown of admin portal access by device type - desktop (blue), tablet (green), and mobile (amber) - over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> High desktop usage is normal for admin tools. Significant tablet/mobile usage (greater than 20%) indicates admins working remotely or need for mobile optimization. If mobile usage is low but desired, improve mobile responsiveness or create a dedicated mobile admin app. High mobile usage with poor mobile UX will frustrate users - prioritize responsive design improvements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessSecurityPage;
